/**
 * The version control's rules, for every screen that draws it: how many versions a line has, how its quantity is shared
 * between them, and what is wrong with the share (order to invoice doc 108 §3.1).
 *
 * The backend is the only place a split is decided (`catalogue_module/models/line_versions.py`). What is here is the
 * preview of that rule, so the screen can show the split and the sum while the person types, in the backend's own
 * words. The backend's field errors, bound to the same inputs, always win when it refuses a request.
 */

/** The dictionary's count field (`Versions`); absent or 1 is one version. */
export const VERSIONS_FIELD_KEY = "Versions";

/** The most versions a line can have. */
export const MAX_VERSIONS = 26;

export type VersionSplitMode = "even" | "entered";

/** What the control holds: the count, how the quantity is shared, and (entered) each version's typed quantity. */
export interface VersionsValue {
  count: number;
  split: VersionSplitMode;
  /** One per version once entered; `null` is a box not yet filled. Ignored while the split is even. */
  entered: (number | null)[];
}

export const ONE_VERSION: VersionsValue = {
  count: 1,
  split: "even",
  entered: [],
};

/** The request fields a line of versions adds (`version_split`, `version_quantities`); empty for one version. */
export interface VersionsRequestFields {
  versionSplit?: VersionSplitMode;
  versionQuantities?: number[];
}

/** A field the backend would refuse the split on, in its own words (`modules/field_errors.py`). */
export interface VersionsProblem {
  field: "quantity" | "version_quantities" | `version_quantities.${number}`;
  code: string;
  message: string;
}

/** The backend's field errors for the version inputs, by the input they belong beside. */
export interface VersionsFieldMessages {
  count?: string;
  split?: string;
  quantities?: string;
}

/**
 * Sort a response's field errors (API body paths, `api-validation.md`) to the version control's three places: the
 * count (`configuration.Versions`), the split (`version_split`), and the quantities (`version_quantities`, or `quantity`
 * when it is too few for the versions). Errors about anything else are left to their own inputs.
 */
export function versionsFieldMessages(
  errors: readonly { field: string; code?: string; message: string }[],
): VersionsFieldMessages {
  const messages: VersionsFieldMessages = {};
  for (const error of errors) {
    if (error.field === `configuration.${VERSIONS_FIELD_KEY}`)
      messages.count ??= error.message;
    else if (error.field === "version_split") messages.split ??= error.message;
    else if (
      error.field === "version_quantities" ||
      error.field.startsWith("version_quantities.") ||
      (error.field === "quantity" && error.code === "fewer_than_versions")
    )
      messages.quantities ??= error.message;
  }
  return messages;
}

/** A, B, C… by position (1-based); past Z it goes AA, AB, like the backend. */
export function versionLabel(position: number): string {
  let label = "";
  let number = position;
  while (number > 0) {
    const rest = (number - 1) % 26;
    label = String.fromCharCode(65 + rest) + label;
    number = Math.floor((number - 1) / 26);
  }
  return label;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/** The step the item's quantities move in: the greatest common divisor of its ladder (50 for 50, 250, 1000), 1 with no ladder. */
export function quantityStepOf(ladderQuantities: readonly number[]): number {
  const rungs = ladderQuantities.filter((q) => q > 0);
  return rungs.length === 0 ? 1 : rungs.reduce(gcd);
}

/** The count a raw field value means: its whole number, clamped to 1 to 26, 1 when empty or not a number. */
export function versionCountOf(raw: string | undefined): number {
  const parsed = Number(raw);
  if (raw === undefined || raw.trim() === "" || !Number.isInteger(parsed))
    return 1;
  return Math.min(Math.max(parsed, 1), MAX_VERSIONS);
}

/**
 * Each version's quantity when `total` is shared evenly in steps of `step` (the remainder, in steps, one each to the
 * first versions: 250 in 3 on a step of 50 is 100, 100, 50); `null` when it cannot be shared that way.
 */
export function evenVersionQuantities(
  total: number,
  count: number,
  step: number,
): number[] | null {
  if (count < 2 || total % step !== 0 || total / step < count) return null;
  const units = Math.floor(total / step / count);
  const remainder = (total / step) % count;
  return Array.from(
    { length: count },
    (_, position) => (units + (position < remainder ? 1 : 0)) * step,
  );
}

/** "3 versions, split evenly: A 334 · B 333 · C 333", as the dry run says it; `null` for one version. */
export function describeVersions(
  quantities: readonly number[],
  split: VersionSplitMode,
): string | null {
  if (quantities.length < 2) return null;
  const how = split === "even" ? "split evenly" : "quantities entered";
  const parts = quantities
    .map((q, i) => `${versionLabel(i + 1)} ${q.toLocaleString("en-GB")}`)
    .join(" · ");
  return `${quantities.length} versions, ${how}: ${parts}`;
}

/** "3 versions: A 334 · B 333 · C 333" for a line already made, by the labels it carries; `null` for one version. */
export function listVersions(
  versions: readonly { label: string; quantity: number }[],
): string | null {
  if (versions.length < 2) return null;
  const parts = versions
    .map((v) => `${v.label} ${v.quantity.toLocaleString("en-GB")}`)
    .join(" · ");
  return `${versions.length} versions: ${parts}`;
}

/**
 * How a route makes a line's versions, beside its summed cost: "3 versions priced separately", or "3 versions in one
 * run" where the route prices them together (in-house); `null` for one version. The backend's dry run says the same
 * (`_versions_phrase`).
 */
export function versionsPriceLabel(
  count: number,
  prices: "separate" | "combined" = "separate",
): string | null {
  if (count < 2) return null;
  return prices === "combined"
    ? `${count} versions in one run`
    : `${count} versions priced separately`;
}

/** The versions' quantities as typed: every box filled with a whole number of at least 1, else `null`. */
function filledQuantities(value: VersionsValue): number[] | null {
  const boxes = value.entered.slice(0, value.count);
  if (boxes.length !== value.count || boxes.some((q) => q === null))
    return null;
  return boxes as number[];
}

/**
 * What the backend would refuse the split on, before it is asked (`version_field_errors`): null when the share is
 * sound or the person is still typing. An unfilled box is not yet wrong.
 */
export function versionsProblem(
  value: VersionsValue,
  total: number,
  step: number,
): VersionsProblem | null {
  if (value.count < 2) return null;
  if (total < value.count) {
    return {
      field: "quantity",
      code: "fewer_than_versions",
      message: `${value.count} versions need a quantity of at least ${value.count}.`,
    };
  }
  if (value.split === "even") {
    return evenVersionQuantities(total, value.count, step) === null
      ? {
          field: "version_quantities",
          code: "cannot_split_evenly",
          message: `${total.toLocaleString("en-GB")} cannot be split evenly into ${value.count} versions in steps of ${step.toLocaleString("en-GB")}: enter the quantity of each version.`,
        }
      : null;
  }
  const boxes = value.entered.slice(0, value.count);
  const badBox = boxes.findIndex((q) => q !== null && q < 1);
  if (badBox >= 0) {
    return {
      field: `version_quantities.${badBox}`,
      code: "must_be_positive",
      message: "Each version needs a quantity of at least 1.",
    };
  }
  const filled = filledQuantities(value);
  if (filled === null) return null;
  const sum = filled.reduce((a, b) => a + b, 0);
  return sum === total
    ? null
    : {
        field: "version_quantities",
        code: "quantities_do_not_add_up",
        message: `The versions' quantities add up to ${sum.toLocaleString("en-GB")}, not ${total.toLocaleString("en-GB")}.`,
      };
}

/**
 * Each version's quantity as the screen shows it: the even share, or what was entered; `null` while the split is
 * unsound or unfinished.
 */
export function versionQuantities(
  value: VersionsValue,
  total: number,
  step: number,
): number[] | null {
  if (value.count < 2) return [total];
  if (value.split === "even")
    return evenVersionQuantities(total, value.count, step);
  const filled = filledQuantities(value);
  return filled !== null && versionsProblem(value, total, step) === null
    ? filled
    : null;
}

/**
 * The request fields for this value. One version sends none. An even split sends only the split (the backend works
 * the quantities out). Entered quantities go once every box is filled; before that the split alone is sent, so the
 * backend names what is missing.
 */
export function versionsRequestFields(
  value: VersionsValue,
): VersionsRequestFields {
  if (value.count < 2) return {};
  if (value.split === "even") return { versionSplit: "even" };
  const filled = filledQuantities(value);
  return filled === null
    ? { versionSplit: "entered" }
    : { versionSplit: "entered", versionQuantities: filled };
}

/** The value after the count changes: entered quantities belong to one count, so a different count starts them again. */
export function withVersionCount(
  value: VersionsValue,
  count: number,
): VersionsValue {
  if (count === value.count) return value;
  return { count, split: count < 2 ? "even" : value.split, entered: [] };
}

/** Switching to entered quantities starts from the even share when there is one, else from empty boxes. */
export function withVersionSplit(
  value: VersionsValue,
  split: VersionSplitMode,
  total: number,
  step: number,
): VersionsValue {
  if (split === value.split) return value;
  if (split === "even") return { ...value, split };
  const even = evenVersionQuantities(total, value.count, step);
  return {
    ...value,
    split,
    entered: even ?? Array.from({ length: value.count }, () => null),
  };
}
